
import React, { useEffect, useState, useCallback, useMemo, use } from "react";
import {
    Box,
    Typography,
    CardContent,
    useTheme,
    useMediaQuery,
    Fade,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Select,
    MenuItem,
    FormControl,
    Button,
    Chip,
    Grid,
    Card,
    styled,
    CircularProgress,
    SelectChangeEvent,
    Tooltip,
    TextField,
    InputAdornment,
} from "@mui/material";
import VerifiedIcon from "@mui/icons-material/Verified";
import AssignmentIcon from "@mui/icons-material/Assignment";
import PersonIcon from "@mui/icons-material/Person";

import AdminApi from "Service/Admin.api";
import { HeaderCard, SearchCard, StyledContainer } from "./ProfessionalUserList";
import ColorData from "Components/CstmTheme/ColorData";
import { useDispatch } from "react-redux";
import { setAlertData, startLoading, stopLoading } from "../../Redux/Screen/Action";
import SearchIcon from '@mui/icons-material/Search';
import { color } from "framer-motion";
import { s } from "framer-motion/dist/types.d-Bq-Qm38R";
import { Padding } from "@mui/icons-material";

type CaseItem = {
    pkCase: string;
    description: string;
    assignedProfessionalId?: string;
    casePrice: number;
    dateCreated: string;
    statusLastUpdated: string;
    client: {
        id: number;
        firstName: string;
        lastName: string;
        email: string;
        contactNumber: string;
    };
    caseType: {
        pkCaseType: number;
        caseTypeName: string;
    };
    orderType: {
        pkOrderType: number;
        orderTypeName: string;
        orderPrice: number;
    };
    assignedProfessional?: {
        id: number;
        firstName: string;
        lastName: string;
    };
};
interface Professional {
    id: string;
    firstName: string;
    lastName: string;
}



const StyledCard = styled(Card)(({ theme }) => ({
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(2),
    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
    border: `1px solid ${theme.palette.divider}`,
    transition: "all 0.3s ease-in-out",
    "&:hover": {
        transform: "translateY(-2px)",
        boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
    },
}));

const AssignButton = styled(Button)(({ theme }) => ({
    borderRadius: theme.spacing(3),
    textTransform: "none",
    fontWeight: "bold",
    padding: theme.spacing(1, 3),
    background: ColorData.primary.main, // <-- Use secondary color for background
    color: ColorData.text.secondary, // <-- Set contrasting text color (black)
    "&:hover": {
        background: ColorData.hover.main, // <-- Use hover color for background on hover
        transform: "translateY(-1px)",
        boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
        color: ColorData.text.primary, // <-- Maintain contrast on hover too
    },
}));

// Custom hook for responsive table configuration
const useResponsiveTable = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isTablet = useMediaQuery(theme.breakpoints.down('md'));
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const tableConfig = useMemo(() => ({
        showScrollbars: isDesktop,
        maxHeight: isMobile ? '60vh' : isTablet ? '65vh' : '70vh',
        minWidth: isMobile ? '100%' : '800px',
        stickyHeader: !isMobile,
        useCardLayout: isMobile || isTablet,
    }), [isMobile, isTablet, isDesktop]);

    return { isMobile, isTablet, isDesktop, tableConfig };
};

// Custom hook for scroll performance monitoring
const useScrollPerformance = () => {
    const [isScrolling, setIsScrolling] = useState(false);
    const scrollTimeoutRef = React.useRef<NodeJS.Timeout | null>(null);

    const handleScroll = useCallback(() => {
        setIsScrolling(true);

        if (scrollTimeoutRef.current) {
            clearTimeout(scrollTimeoutRef.current);
        }

        scrollTimeoutRef.current = setTimeout(() => {
            setIsScrolling(false);
        }, 150);
    }, []);

    React.useEffect(() => {
        return () => {
            if (scrollTimeoutRef.current) {
                clearTimeout(scrollTimeoutRef.current);
            }
        };
    }, []);

    return { isScrolling, handleScroll };
};

const AssignProfessional: React.FC = () => {
    const [caseList, setCaseList] = useState<CaseItem[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<CaseItem[]>([]);
    const [professionalList, setProfessionalList] = useState<Professional[]>([]);
    const [selectedProfessionals, setSelectedProfessionals] = useState<Record<string, string>>({});
    const [loading, setLoading] = useState(false);
    const [searchQuery, setSearchQuery] = useState("");
    const [assignLoading, setAssignLoading] = useState<Record<string, boolean>>({});
    const dispatch = useDispatch();
    const theme = useTheme();

    // Use responsive table configuration
    const { isMobile, isTablet, tableConfig } = useResponsiveTable();
    const { isScrolling, handleScroll } = useScrollPerformance();

    // Keyboard navigation state
    const [focusedCell, setFocusedCell] = useState<{ row: number; col: number } | null>(null);

    const ITEM_HEIGHT = 40; // Adjust if needed

    // Keyboard navigation handler
    const handleKeyboardNavigation = useCallback((event: React.KeyboardEvent, rowIndex: number, colIndex: number) => {
        const { key, ctrlKey } = event;
        const totalRows = caseList.length;
        const totalCols = 8; // Number of columns

        let newRow = rowIndex;
        let newCol = colIndex;

        switch (key) {
            case 'ArrowRight':
                event.preventDefault();
                newCol = Math.min(colIndex + 1, totalCols - 1);
                break;
            case 'ArrowLeft':
                event.preventDefault();
                newCol = Math.max(colIndex - 1, 0);
                break;
            case 'ArrowDown':
                event.preventDefault();
                newRow = Math.min(rowIndex + 1, totalRows - 1);
                break;
            case 'ArrowUp':
                event.preventDefault();
                newRow = Math.max(rowIndex - 1, 0);
                break;
            case 'Home':
                event.preventDefault();
                if (ctrlKey) {
                    newRow = 0;
                    newCol = 0;
                } else {
                    newCol = 0;
                }
                break;
            case 'End':
                event.preventDefault();
                if (ctrlKey) {
                    newRow = totalRows - 1;
                    newCol = totalCols - 1;
                } else {
                    newCol = totalCols - 1;
                }
                break;
            default:
                return;
        }

        setFocusedCell({ row: newRow, col: newCol });

        // Focus the new cell
        const cellId = `cell-${newRow}-${newCol}`;
        const cellElement = document.getElementById(cellId);
        if (cellElement) {
            cellElement.focus();
        }
    }, [caseList.length]);

    useEffect(() => {
        fetchData();
    }, [])

    useEffect(() => {
        if (searchQuery === "") {
            fetchCases();
        }
        else {
            const filterdCaseList = caseList.filter((caseItem) => {
                const fullName = `${caseItem.client.firstName} ${caseItem.client.lastName}`.toLowerCase();
                const phone = caseItem.client.contactNumber.toLowerCase();
                const orderType = caseItem.orderType.orderTypeName.toLowerCase();
                const caseType = caseItem.caseType.caseTypeName.toLowerCase();

                return (
                    fullName.includes(searchQuery.toLowerCase()) ||
                    phone.includes(searchQuery.toLowerCase()) ||
                    orderType.includes(searchQuery.toLowerCase()) ||
                    caseType.includes(searchQuery.toLowerCase())
                )
            });
            setFilteredUsers(filterdCaseList as any);
        }

    }, [searchQuery])
    const fetchData = async () => {
        setLoading(true);
        try {
            await Promise.all([fetchCases(), fetchVerifyProfessionalList()]);
        } catch (error) {
            console.error("Error fetching data:", error);
        } finally {
            setLoading(false);
        }
    };

    const fetchCases = async () => {
        const res = await AdminApi.fetchCases();
        console.log("Fetched cases:", res);
        if (res?.success && Array.isArray(res.data)) {
            const sortedClass = res.data.sort((a: any, b: any) => b.pkCase - a.pkCase);
            setCaseList(sortedClass);
            setFilteredUsers(sortedClass as any);
        }
        // if (res?.success) setCaseList(res.data);
    };

    const fetchVerifyProfessionalList = async () => {
        const res = await AdminApi.fetchVerifyProfessionalList();
        if (res?.success) setProfessionalList(res.data);
    };

    const handleProfessionalChange = (caseId: string, value: string) => {
        setSelectedProfessionals((prev) => ({
            ...prev,
            [caseId]: value,
        }));
    };

    const handleAssignProfessional = async (caseId: string) => {
        const professionalId = selectedProfessionals[caseId];
        try {
            dispatch(startLoading(
                {
                    loading: true,
                    loadtext: "Assigning Professional...",
                }
            ))
            const AssignCaseToProfessional = await AdminApi.AssignCaseToProfessional({
                caseId: caseId,
                assignedProfessionalId: professionalId,
            })
            if (AssignCaseToProfessional?.success) {
                dispatch(stopLoading());
                dispatch(
                    setAlertData({
                        alertMessage: AssignCaseToProfessional?.alert.alertMessage,
                        alertVariant: AssignCaseToProfessional?.alert.alertVariant,
                        openAlert: AssignCaseToProfessional?.alert.openAlert,
                    })
                );
            }
            fetchCases();
        } catch (error) {
            console.error("Error assigning professional:", error);
            return;
        }
    };

    const renderMobileCard = (caseItem: CaseItem, index: number) => (
        <StyledCard key={caseItem.pkCase}>
            <CardContent sx={{ p: 3 }}>
                <Box display="flex" justifyContent="space-between" mb={2}>
                    <Chip label={`#${index + 1}`} size="small" color="primary" sx={{ fontWeight: "bold" }} />
                    <Chip
                        label={caseItem.orderType.orderTypeName}
                        variant="outlined"
                        size="small"
                        color="secondary"
                    />
                </Box>

                <Typography variant="h6" color="primary" fontWeight="bold">
                    {caseItem.caseType.caseTypeName}
                </Typography>

                <Box mb={2}>
                    <Typography variant="body2" gutterBottom>
                        <strong>Client:</strong> {caseItem.client.firstName} {caseItem.client.lastName}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>Phone:</strong> {caseItem.client.contactNumber}
                    </Typography>
                    <Typography variant="body2" gutterBottom>
                        <strong>Description:</strong> {caseItem.description}
                    </Typography>
                </Box>

                {caseItem.assignedProfessionalId ? (
                    // Show disabled select if assigned
                    <FormControl fullWidth size="small" disabled>
                        <Select
                            value={caseItem.assignedProfessionalId}
                            startAdornment={<PersonIcon sx={{ mr: 1, color: "action.active" }} />}
                        >
                            <MenuItem value={caseItem.assignedProfessionalId}>
                                {
                                    professionalList.find(
                                        (pro) => pro.id === caseItem.assignedProfessionalId
                                    )?.firstName
                                }{" "}
                                {
                                    professionalList.find(
                                        (pro) => pro.id === caseItem.assignedProfessionalId
                                    )?.lastName
                                }
                            </MenuItem>
                        </Select>
                    </FormControl>
                ) : (
                    <>
                        {/* Show active dropdown */}
                        <FormControl fullWidth size="small">
                            <Select
                                value={selectedProfessionals[caseItem.pkCase] || ""}
                                onChange={(e: SelectChangeEvent) =>
                                    handleProfessionalChange(caseItem.pkCase, e.target.value)
                                }
                                displayEmpty
                                startAdornment={<PersonIcon sx={{ mr: 1, color: "action.active" }} />}
                            >
                                <MenuItem value="" disabled>
                                    Select Professional
                                </MenuItem>
                                {professionalList.map((pro) => (
                                    <MenuItem key={pro.id} value={pro.id}>
                                        {pro.firstName} {pro.lastName}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Assign button */}
                        <AssignButton
                            variant="contained"
                            fullWidth
                            onClick={() => handleAssignProfessional(caseItem.pkCase)}
                            disabled={
                                !selectedProfessionals[caseItem.pkCase] || assignLoading[caseItem.pkCase]
                            }
                            startIcon={
                                assignLoading[caseItem.pkCase] ? (
                                    <CircularProgress size={16} />
                                ) : (
                                    <AssignmentIcon />
                                )
                            }
                            sx={{ mt: 2 }}
                        >
                            {assignLoading[caseItem.pkCase] ? "Assigning..." : "Assign Professional"}
                        </AssignButton>
                    </>
                )}
            </CardContent>
        </StyledCard>
    );


    const renderDesktopTable = () => (
        <TableContainer
            sx={{
                maxHeight: 470,
                overflowY: "auto",
                border: "1px solid #e0e0e0",
                borderRadius: 2,
            }}
        >
            <Table stickyHeader aria-label="Cases assignment table">
                {/* TABLE HEAD */}
                <TableHead>
                    <TableRow
                        sx={{
                            "& .MuiTableCell-root": {
                                backgroundColor: ColorData.primary.main + " !important", // Force override
                                color: ColorData.text.secondary, // white
                                fontWeight: "bold",
                                textTransform: "uppercase",
                                fontSize: "0.875rem",
                                borderBottom: `1px solid ${ColorData.primary.strokeColor}`,
                            },
                        }}
                    >
                        <TableCell>Case No.</TableCell>
                        <TableCell>Order Type</TableCell>
                        <TableCell>Case Type</TableCell>
                        <TableCell>Client Name</TableCell>
                        <TableCell>Client Phone</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="center">Assign Professional</TableCell>
                        <TableCell align="center">Action</TableCell>
                    </TableRow>
                </TableHead>

                {/* TABLE BODY */}
                <TableBody>
                    {filteredUsers.map((item, rowIndex) => (
                        <TableRow
                            key={item.pkCase}
                            hover
                            sx={{
                                backgroundColor: ColorData.background.default,
                                transition: "background-color 0.3s, transform 0.3s",
                                "&:hover": {
                                    backgroundColor: ColorData.hover.main,
                                    transform: "translateY(-2px)",
                                    boxShadow: "0 8px 30px rgba(0,0,0,0.12)",
                                },
                            }}
                        >
                            <TableCell>
                                <Chip label={item.pkCase} size="small" color="primary" />
                            </TableCell>
                            <TableCell>
                                <Chip
                                    label={item.orderType.orderTypeName}
                                    variant="outlined"
                                    size="small"
                                    color="primary"
                                />
                            </TableCell>
                            <TableCell>
                                <Typography variant="body2" fontWeight="medium" color="primary">
                                    {item.caseType.caseTypeName}
                                </Typography>
                            </TableCell>
                            <TableCell>
                                {item.client.firstName} {item.client.lastName}
                            </TableCell>
                            <TableCell>{item.client.contactNumber}</TableCell>
                            <TableCell>
                                <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                                    {item.description}
                                </Typography>
                            </TableCell>

                            {/* ASSIGN PROFESSIONAL */}
                            <TableCell align="center">
                                {item.assignedProfessionalId ? (
                                    <FormControl size="small" fullWidth={false} sx={{ width: 160 }} disabled>
                                        <Select
                                            value={item.assignedProfessionalId}
                                            startAdornment={<PersonIcon sx={{ mr: 1, color: "action.active" }} />}
                                        >
                                            <MenuItem value={item.assignedProfessionalId}>
                                                {
                                                    professionalList.find(
                                                        (pro) => pro.id === item.assignedProfessionalId
                                                    )?.firstName
                                                }{" "}
                                                {
                                                    professionalList.find(
                                                        (pro) => pro.id === item.assignedProfessionalId
                                                    )?.lastName
                                                }
                                            </MenuItem>
                                        </Select>
                                    </FormControl>
                                ) : (
                                    <FormControl size="small" sx={{ width: 160 }}>
                                        <Select
                                            value={selectedProfessionals[item.pkCase] || ""}
                                            onChange={(e) =>
                                                handleProfessionalChange(item.pkCase, e.target.value)
                                            }
                                            displayEmpty
                                            startAdornment={<PersonIcon sx={{ mr: 1, color: "action.active" }} />}
                                        >
                                            <MenuItem value="" disabled>
                                                Select Professional
                                            </MenuItem>
                                            {professionalList.map((pro) => (
                                                <MenuItem key={pro.id} value={pro.id}>
                                                    {pro.firstName} {pro.lastName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                )}
                            </TableCell>

                            {/* ACTION */}
                            <TableCell align="center">
                                <Button
                                    variant="contained"
                                    size="small"
                                    onClick={() => handleAssignProfessional(item.pkCase)}
                                    disabled={!selectedProfessionals[item.pkCase] || assignLoading[item.pkCase]}
                                    startIcon={
                                        assignLoading[item.pkCase] ? (
                                            <CircularProgress size={16} />
                                        ) : (
                                            <AssignmentIcon />
                                        )
                                    }
                                >
                                    {assignLoading[item.pkCase] ? "Assigning..." : "Assign"}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );

    if (loading) {
        return (
            <StyledContainer maxWidth="xl">
                <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                    <CircularProgress size={60} />
                </Box>
            </StyledContainer>
        );
    }

    return (
        <Box sx={{ padding: 1 }}>
             <Typography variant="h5" fontWeight="bold" gutterBottom sx={{
                    padding: 2,
                    borderRadius: 2,
                    backgroundColor: theme => ColorData.primary.main,
                    color: theme => ColorData.text.secondary,
                  }}>
                    Assign Professional to Case
                  </Typography>

            {/* Search and Actions */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid size={{ xs: 12, md: 8 }}>
                    {/* <SearchCard elevation={2}>
                                <CardContent> */}
                    <TextField
                        fullWidth
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        placeholder="Search professional User..."
                        variant="outlined"
                        slotProps={{
                            input: {
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="primary" />
                                    </InputAdornment>
                                ),
                            },
                        }}
                        sx={{
                            '& .MuiOutlinedInput-root': {
                                borderRadius: 3,
                                '&:hover fieldset': {
                                    borderColor: theme.palette.primary.main,
                                },
                            },
                        }}
                    />
                    {/* </CardContent>
                            </SearchCard> */}
                </Grid>

                <Grid size={{ xs: 12, md: 4 }}>
                    <Box display="flex" gap={1} height="100%" alignItems="center">
                        <Chip
                            icon={<PersonIcon />}
                            label={`Total Users: ${professionalList.length}`}
                            color="info"
                            variant="outlined"
                            sx={{
                                fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                height: { xs: 28, sm: 32 }
                            }}
                        />
                    </Box>
                </Grid>
            </Grid>

            {filteredUsers.length === 0 ? (
                <Box textAlign="center" py={8}>
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        No cases available
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Cases will appear here once they are created.
                    </Typography>
                </Box>
            ) : (
                tableConfig.useCardLayout ? (
                    <Box
                        sx={{
                            maxHeight: tableConfig.maxHeight,
                            overflow: 'auto',
                            WebkitOverflowScrolling: 'touch', // iOS smooth scrolling
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: theme.palette.grey[400],
                                borderRadius: '3px',
                                '&:hover': {
                                    background: theme.palette.grey[600],
                                },
                            },
                        }}
                    >
                        <Grid container spacing={2}>
                            {filteredUsers.map((caseItem, index) => (
                                <Grid key={caseItem.pkCase}>
                                    {renderMobileCard(caseItem, index)}
                                </Grid>
                            ))}
                        </Grid>
                    </Box>
                ) : (
                    renderDesktopTable()
                )
            )}
        </Box>
        
    );
};

export default AssignProfessional;
